import { BooleanOptions } from "src/helpers/const";
import type { AdminFilterUsersInput, UserLocation } from "src/types/graphql";

/**
 * The filter object the Members page builds from URL search params. URL
 * params are strings, so everything in here arrives string-shaped.
 */
export interface UsersPageFilters {
  subscriptionStatus: string[];
  plan: string[];
  kind: string[];
  location: UserLocation[];
  interests: string[];
  englishSkills: string[];
  englishLevels: string[];
  openToSpeakingPartners: string[];
  occupations: string[];
  industries: string[];
  badges: string[];
  verificationStatus: string[];
  zones: string[];
  tenures: string[];
  minEventsAttended: string;
  storyKeyword: string;
  cancelledInRecovery: boolean;
}

/**
 * Converts the Members page's URL-derived filters into the shape the API's
 * `AdminFilterUsersInput` accepts: booleans for the verification /
 * open-to-speaking-partners toggles, an integer — or nothing at all — for
 * minimum events attended.
 *
 * Every caller that turns these filters into a GraphQL variable (members
 * table, wider-search probe, CSV export, copy emails, bulk-invite validate)
 * must go through here. Spreading the raw object instead ships a
 * `minEventsAttended` string that the Int input rejects during variable
 * coercion — the request dies before the resolver even runs, and production
 * masks that as "Unexpected server error".
 *
 * The one cast is this helper's whole job: the URL carries the values the
 * filters form wrote as plain strings, and only here do they become the
 * API-typed values (the three fields above are genuinely converted; the
 * rest pass through unchanged).
 */
export const toApiUsersFilters = (filters: UsersPageFilters): AdminFilterUsersInput =>
  ({
    ...filters,
    verificationStatus: filters.verificationStatus.map((option) => option === BooleanOptions.True),
    openToSpeakingPartners: filters.openToSpeakingPartners.map(
      (option) => option === BooleanOptions.True,
    ),
    // The URL carries this as a string; the API wants a number, and an empty
    // box means "not set", not zero.
    minEventsAttended: filters.minEventsAttended === "" ? null : Number(filters.minEventsAttended),
  }) as AdminFilterUsersInput;

/**
 * The fully prepared filters for every API call the Members page makes: the
 * URL-derived filters converted, plus what lives outside the URL — the search
 * box and the population scope (the Current / Recovery / Everyone tab, unless
 * statuses were picked by hand in Advanced Search).
 *
 * Everything the page sends to the API — members table, wider-search probe,
 * CSV export, copy emails, bulk invite — starts from this object, so what the
 * operator sees on screen is exactly what gets queried, exported and invited.
 */
export const buildUsersPageApiFilters = (
  filters: UsersPageFilters,
  scope: { query: string; subscriptionStatus: string[] },
): AdminFilterUsersInput =>
  ({
    ...toApiUsersFilters(filters),
    query: scope.query,
    subscriptionStatus: scope.subscriptionStatus,
  }) as AdminFilterUsersInput;
